import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../styles/theme';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing['4xl']} ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['5xl']};
  margin-bottom: ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing['4xl']};
  margin-bottom: ${theme.spacing['4xl']};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing['2xl']};
  }
`;

const ContactInfo = styled.div`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['3xl']};
  backdrop-filter: blur(10px);
`;

const ContactForm = styled.form`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['3xl']};
  backdrop-filter: blur(10px);
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing['2xl']};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
  color: ${theme.colors.primary.light};
  margin-right: ${theme.spacing.lg};
  min-width: 2rem;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h4`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
`;

const InfoText = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing['2xl']};
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.base};
  transition: ${theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.md};
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.base};
  min-height: 120px;
  resize: vertical;
  transition: ${theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
  color: white;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: ${theme.transitions.default};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin: 0 auto;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled(motion.div)`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SuccessMessage = styled(StatusMessage)`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  color: ${theme.colors.success.light};
`;

const ErrorMessage = styled(StatusMessage)`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: ${theme.colors.error.light};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['3xl']};
  margin-bottom: ${theme.spacing['2xl']};
  color: ${theme.colors.text.primary};
  text-align: center;
`;

const PoweredBy = styled.div`
  text-align: center;
  padding: ${theme.spacing.lg};
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius.xl};
  backdrop-filter: blur(10px);
`;

const PoweredByText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const PoweredByLink = styled.a`
  color: ${theme.colors.primary.light};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.semibold};
  
  &:hover {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStatus({
        type: 'success',
        message: 'Thank you for your message! We\'ll get back to you at boatwise@modernmarine.ca within 24 hours.'
      });
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Get in Touch</Title>
        <Subtitle>
          Have questions about BoatWise? Need support? We're here to help you get the most out of your marine monitoring experience.
        </Subtitle>
      </Header>

      <ContactGrid>
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <ContactInfo>
            <InfoItem>
              <InfoIcon><FaEnvelope /></InfoIcon>
              <InfoContent>
                <InfoTitle>Email Us</InfoTitle>
                <InfoText>boatwise@modernmarine.ca</InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon><FaPhone /></InfoIcon>
              <InfoContent>
                <InfoTitle>Call Us</InfoTitle>
                <InfoText>416-570-6895</InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon><FaMapMarkerAlt /></InfoIcon>
              <InfoContent>
                <InfoTitle>Visit Us</InfoTitle>
                <InfoText>Port Perry, ON<br />We travel with our portable workshop!</InfoText>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon><FaClock /></InfoIcon>
              <InfoContent>
                <InfoTitle>Business Hours</InfoTitle>
                <InfoText>Monday - Friday: 8:00 AM - 6:00 PM EST<br />Saturday: 9:00 AM - 4:00 PM EST</InfoText>
              </InfoContent>
            </InfoItem>
          </ContactInfo>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <ContactForm onSubmit={handleSubmit}>
            {status && (
              status.type === 'success' ? (
                <SuccessMessage
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <FaCheckCircle />
                  {status.message}
                </SuccessMessage>
              ) : (
                <ErrorMessage
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <FaExclamationCircle />
                  {status.message}
                </ErrorMessage>
              )
            )}

            <FormGroup>
              <Label htmlFor="name">Name</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="How can we help you?"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us more about your question or concern..."
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              <FaPaperPlane />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </SubmitButton>
          </ContactForm>
        </motion.div>
      </ContactGrid>

      <Section>
        <SectionTitle>Powered by Modern Marine</SectionTitle>
        <PoweredBy>
          <PoweredByText>
            BoatWise is developed and supported by <PoweredByLink href="https://www.modernmarine.ca" target="_blank" rel="noopener noreferrer">Modern Marine</PoweredByLink>, 
            your trusted partner in marine technology solutions. With years of experience in the marine industry, 
            we're committed to providing exceptional support and service to the boating community.
          </PoweredByText>
        </PoweredBy>
      </Section>
    </Container>
  );
};

export default Contact;
